import { useState } from "react";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { useEffect } from "react";
import toast from "react-hot-toast";

const useTenantListing = ({ type }) => {
  const { authData } = useSelector((state) => state.loginSlice);
  const [allTenants, setAllTenants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchEmail, setsearchEmail] = useState("");
  const [tenantsPerPage, setTenantsPerPage] = useState(10);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");

  const [excludeInactive, setExcludeInactive] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [openPurge, setOpenPurge] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [purgeLoader, setPurgeLoader] = useState(false);
  const handleDelete = () => {
    setShowConfirm(true);
  };
  const dispatch = useDispatch();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 500); // Debounce delay (500ms)

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmail(searchEmail);
    }, 500); // Debounce delay (500ms)

    return () => {
      clearTimeout(handler);
    };
  }, [searchEmail]);

  const fetchTenants = async (
    tenantsPerPage,
    currentPage,
    searchTerm,
    excludeInactive
  ) => {
    setLoader(true);
    try {
      let url = `${endpoint.GET_TENANTS}?PageNumber=${currentPage}&PageSize=${tenantsPerPage}&listType=${type}`;

      if (searchTerm) {
        url += `&sBrandName=${searchTerm}`;
      }
      if (searchEmail) {
        url += `&sTenantEmail=${searchEmail}`;
      }
      if (excludeInactive) {
        url += `&excludeInactive=${excludeInactive}`;
      }

      const response = await apiHandler({
        url: url,
        method: "GET",
        authToken: authData,
        dispatch,
      });

      if (response.data.success) {
        setAllTenants(response.data.data.tenants);
        setTotalCount(response.data.data.totalCount);
      }
    } catch (error) {
 
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchTenants(tenantsPerPage, currentPage, debouncedTerm, excludeInactive);
  }, [
    tenantsPerPage,
    currentPage,
    debouncedTerm,
    excludeInactive,
    debouncedEmail,
    type,
  ]);

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = (e) => {
    let value = e.target.value;

    if (value.startsWith(" ")) return;
    const isValid = /^[a-zA-Z0-9 ]*$/.test(value);

    if (!isValid) {
      return;
    }
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    setTenantsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleExcludeInactiveChange = () => {
    setExcludeInactive(!excludeInactive);
    setCurrentPage(1);
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const exportToExcel = () => {
    const headers = [
      [
        "Company Name",
        "Email",
        "Status",
        "Created At",
        "Updated At",
        "Subscription ID",
        "Plan ID",
        "Start Date",
        "Is Active",
        "Renewal Date",
      ],
    ];

    const dataToExport = allTenants.map((tenant) => [
      tenant.sCompanyName,
      tenant.sEmail,
      tenant.sStatus,
      tenant.dtCreatedAt,
      tenant.dtUpdatedAt,
      tenant.subscription?.iSubscriptionId || "",
      tenant.subscription?.iPlanId || "",
      tenant.subscription?.dtStartDate || "",
      tenant.subscription?.bIsActive ? "Yes" : "No",
      tenant.subscription?.dtRenewalDate || "",
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...dataToExport]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tenants");
    XLSX.writeFile(workbook, "Tenant_List_With_Subscription.xlsx");
  };

  function getCutoffDate(days) {
    if (!days) return;
    const today = new Date();
    today.setDate(today.getDate() - days);
    const cutoffDate = today?.toISOString()?.split("T")[0];
    return cutoffDate;
  }

  const handlePurge = async () => {
    const date =
      selectedOption === "custom"
        ? customDate
        : getCutoffDate(parseInt(selectedOption));
    try {
      setPurgeLoader(true);
      const response = await apiHandler({
        url: endpoint.PURGE_TENANTS,
        dispatch,
        authToken: authData,
        method: "POST",
        data: {
          cutoffDate: date,
        },
      });
      setOpenPurge(false);
      setCustomDate("");
      setSelectedOption("");
      fetchTenants();
      setShowConfirm(false);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
   
    } finally {
      setPurgeLoader(false);
    }
  };

  return {
    allTenants,
    currentPage,
    totalCount,
    searchTerm,
    tenantsPerPage,
    excludeInactive,
    activeCount,
    inactiveCount,
    isModalOpen,
    loader,
    handlePageClick,
    handleSearch,
    handlePageSizeChange,
    handleExcludeInactiveChange,
    openModal,
    closeModal,
    exportToExcel,
    setCurrentPage,
    setSearchTerm,
    fetchTenants,
    searchEmail,
    setsearchEmail,
    openPurge,
    setOpenPurge,
    selectedOption,
    setSelectedOption,
    customDate,
    setCustomDate,
    showConfirm,
    handleDelete,
    setShowConfirm,
    handlePurge,
    getCutoffDate,
    purgeLoader,
  };
};

export default useTenantListing;
